import { defineStore } from 'pinia';
import useCommonStore from '@/common/store/pinia/common';
import VhsNodeMapper, { VhsNodeMapped } from '@/common/store/pinia/vhsNodes/vhsNodeMapper';
import { BackendTask, NodeDatastoreBatchAssign, VhsComputeNodeVirtualNetwork, VhsDefaultDatastores, VhsNode, VhsNodeStatus, VhsNodeUpdate } from '@schema';
import api from '@/common/utils/api';
import threadRequests from '@/common/utils/threadRequests';
import vhsNotificationHandler from '@/common/utils/vhsNotificationHandler';

export type VhsComputeNodeReference = {
  node_id: string;
  vns?: Array<VhsComputeNodeVirtualNetwork>;
  ve_datastore_id?: Nullable<string>;
  backup_datastore_id?: Nullable<string>;
  image_datastore_id?: Nullable<string>;
};

export type VhsComputeNodePayload = {
  node_id: string;
  vns?: Array<VhsComputeNodeVirtualNetwork>;
  datastores: NodeDatastoreBatchAssign;
  default_datastores: VhsDefaultDatastores;
};

export const useVhsNodesStore = defineStore('vhsNodes', () => {
  const commonStore = useCommonStore<VhsNode, VhsNodeMapped>({
    getUrl: () => '/api/v2/vhs/nodes',
    mapItem: VhsNodeMapper,
    dataChangeMode: 'event',
  })();

  const addNodes = async (nodes: VhsComputeNodePayload[]) => {
    const tasks: BackendTask[] = [];
    await threadRequests(nodes
      .map(node => () => api
        .post<BackendTask>('/api/v2/vhs/nodes', node)
        .then(resp => tasks.push(resp.data))));
    return tasks;
  };

  const takeLastItem = async ({ id }) => {
    commonStore.fetchLastItem(id);
  };

  // PATCH body type VhsNodeUpdate is intentionally looser than resource type VhsNode for default_datastores.
  // dataChangeMode='event' refreshes the store from the socket `updated` event
  // (onSocket.updated → takeLastItem); callers that need it sooner refetch
  // themselves (the wizard navigates to the list, the default-locations dialog
  // calls fetchAllItems), so no per-item refetch is needed here.
  const update = (nodeId: string, payload: VhsNodeUpdate) => commonStore.update(nodeId, payload as Partial<VhsNode>);

  const onSocket = vhsNotificationHandler('vhs node', {
    created: takeLastItem,
    updated: takeLastItem,
    deleted: ({ id }) => commonStore.abortAndRemoveItem(id),
    'state.updated': ({ id, state }) => commonStore.changeItem({ id, status: state as VhsNodeStatus }),
  });

  return {
    ...commonStore,
    addNodes,
    update,
    onSocket,
  };
}, {
  ws: {
    vhs: 'onSocket'
  }
});

export default useVhsNodesStore;
